/**
 * Box photos API client.
 */
import { supabase } from '../auth/supabase';
import type { BoxPhoto } from '@shared/types';

const API_BASE = '/api/v1';

export class ApiClientError extends Error {
  constructor(
    public statusCode: number,
    public errorType: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

async function getAuthToken(): Promise<string | null> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  return session?.access_token ?? null;
}

export async function uploadBoxPhoto(
  boxId: string,
  file: File,
  caption?: string,
): Promise<BoxPhoto> {
  const token = await getAuthToken();
  const formData = new FormData();
  formData.append('file', file);
  if (caption) formData.append('caption', caption);

  const response = await fetch(`${API_BASE}/boxes/${boxId}/photos`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new ApiClientError(response.status, response.statusText, errorText || 'Upload failed');
  }

  return response.json() as Promise<BoxPhoto>;
}

export async function deleteBoxPhoto(photoId: string): Promise<void> {
  const token = await getAuthToken();

  const response = await fetch(`${API_BASE}/box-photos/${photoId}`, {
    method: 'DELETE',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  if (!response.ok) {
    throw new ApiClientError(response.status, response.statusText, 'Failed to delete photo');
  }
}
